import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Typography, Stack, Skeleton, alpha } from '@mui/material';
import { useQuery } from '@apollo/client/react';
import useAuth from '../../../hooks/useAuth';
import useLocales from '../../../hooks/useLocales';
import { PATH_MAIN } from '../../../routes/paths';
import MyAvatar from '../../../components/MyAvatar';
import { FC } from 'react';
import { GET_REACHLINK_PLUS_SUBSCRIPTION } from '../../../graphql/resolvers/queries';
import { Subscription } from '../../../@types/shared.types';

interface ClientNavbarAccountProps {
    isCollapse?: boolean;
}

const ClientNavbarAccount: FC<ClientNavbarAccountProps> = ({ isCollapse }) => {
    const { user } = useAuth();
    const { translate } = useLocales();

    const isReachlinkPlusOffered = user?.reachlinkPlusEnabled !== false;

    const { data, loading } = useQuery<{ subscription: Subscription }>(
        GET_REACHLINK_PLUS_SUBSCRIPTION,
        { skip: !isReachlinkPlusOffered }
    );

    const isSubscribed = data?.subscription?.isActive;

    return (
        <Link underline="none" color="inherit" component={RouterLink} to={PATH_MAIN.account}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isCollapse ? 'center' : 'flex-start',
                    py: 3,
                    px: isCollapse ? 1 : 3,
                    borderTopLeftRadius: (theme) => Number(theme.shape.borderRadius) * 1.5,
                    borderTopRightRadius: (theme) => Number(theme.shape.borderRadius) * 1.5,
                    border: 0,
                    borderTop: 1,
                    borderStyle: 'solid',
                    borderColor: (theme) => alpha(theme.palette.divider, 0.12),
                    transition: (theme) =>
                        theme.transitions.create('opacity', {
                            duration: theme.transitions.duration.shorter,
                        }),
                }}
            >
                <MyAvatar />

                {!isCollapse && (
                    <Stack
                        sx={{
                            ml: 2,
                            gap: 0.5,
                        }}
                    >
                        <Typography variant="subtitle2">{user?.name}</Typography>
                        {!isReachlinkPlusOffered ? null : loading ? (
                            <Skeleton variant="text" width={70} height={16} />
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box
                                    component="img"
                                    src={
                                        isSubscribed
                                            ? '/assets/illustrations/rl-plus.svg'
                                            : '/assets/illustrations/sparkles.svg'
                                    }
                                    alt=""
                                    aria-hidden="true"
                                    sx={{ width: 14, height: 14 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    {translate(
                                        isSubscribed
                                            ? 'layouts.clientNavbarAccount.reachLinkPlus'
                                            : 'layouts.clientNavbarAccount.freePlan'
                                    )}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                )}
            </Box>
        </Link>
    );
};

export default ClientNavbarAccount;
